/**
 * Unified API Client — TanStack Query hooks for all `apps/web` backend
 * data fetching (GitHub issue — "Unified API client with RTK Query for
 * all frontend data fetching", extended by "Implement RTK Query hooks
 * and caching for all API endpoints" to cover the admin console's
 * dashboard/customers/jobs/job-detail endpoints and the impersonation
 * start/end mutations).
 *
 * This is the single module every component should import typed query/
 * mutation hooks from, replacing ad hoc `fetch()` calls scattered across
 * route components (see, e.g., the pre-migration versions of
 * `app/routes/admin/Subscriptions.tsx`, `app/routes/admin/AdminDashboard.tsx`,
 * `app/routes/admin/CustomersList.tsx`, `app/routes/admin/JobsList.tsx`,
 * `app/routes/admin/JobDetail.tsx`, and `app/routes/account/Subscription.tsx`
 * — all of which either called `fetch()` directly with hand-rolled
 * loading/error `useState`, or rendered hard-coded mock data behind a
 * `// TODO: Call GET /api/admin/...` comment).
 *
 * Why "TanStack Query", not "RTK Query": neither Redux Toolkit nor RTK
 * Query, nor axios, exist anywhere in this codebase — this v2 rewrite's
 * canonical data-fetching stack (see this issue's own "Why", and every
 * prior issue's Zustand + `@tanstack/react-query` usage — e.g.
 * `src/app/routes/Subscription.tsx`, `src/stores/authStore.ts`) is
 * Zustand (client state) + TanStack Query (server state). "RTK Query" in
 * this issue's title is this milestone's legacy/inherited naming for
 * "the one unified query client"; there is no RTK Query code to migrate
 * *from* here, so this module supersedes the ad hoc `fetch()` call sites
 * enumerated above and the "superseded by RTK Query" placeholder note in
 * `./fetchClient.ts`'s module doc-comment (that TODO now points here).
 *
 * File-naming note (this issue's technical spec sketches new
 * `src/lib/api/hooks.ts` / `src/stores/apiStores.ts` files): extending
 * this already-existing `apiClient.ts` / the sibling `@/stores/apiStore`
 * (singular) — rather than adding parallel, literally-named files — for
 * the same reason the immediately-preceding "Unified API client with RTK
 * Query" issue named this module `apiClient.ts` instead of a literal
 * spec name: every hook/mutation below shares one `QueryClient`
 * (`queryClient`, mounted once in `app/AppShell.tsx`) and one query-key
 * factory (`apiQueryKeys`) with the subscriptions hooks already here — a
 * second, separately-named module would either duplicate that
 * `QueryClient`/`useApiFetch` wiring or awkwardly re-export from this
 * file, re-introducing exactly the "two parallel API clients" problem
 * this milestone's issues exist to eliminate (see `src/lib/api/soloApi.ts`'s
 * module doc-comment for the identical reasoning applied to the root
 * `src/` tree).
 *
 * `apiClient.ts` naming note: `src/lib/api/apiClient.ts` (root `src/`
 * tree, a different workspace/package) already exists and is scoped
 * entirely to Cloudflare Vectorize operations (GH issue #293) — an
 * unrelated, previously-shipped concern in a different `tsconfig`/build
 * root. This file is `apps/web`'s own `src/lib/api/apiClient.ts` (the
 * live, deployed SPA's copy, per every prior issue's "the live React
 * application is `apps/web`" scope note — see e.g.
 * `src/app/routes/admin/subscriptions.tsx`'s module doc-comment) and does
 * not touch that unrelated module.
 *
 * Building blocks reused (not duplicated):
 * - `./fetchClient`'s `useApiFetch()` / `ApiError` — runtime-config-aware
 *   base URL resolution + Supabase Auth bearer token injection (already
 *   correct; Requirement #7 — "Maintain existing authentication ...
 *   ensuring tokens are injected properly").
 * - `@/lib/validation/subscriptions` / `@/lib/validation/admin` — Zod
 *   schemas (Requirement #4); every hook here `.parse()`s the raw JSON
 *   response so a shape drift between frontend and backend throws
 *   immediately instead of silently rendering `undefined`.
 * - `@/stores/apiStore` — Zustand store for the client-side filter/sort/
 *   pagination state that shapes the admin subscriptions/customers/jobs
 *   queries, and the mutation-derived impersonation session state
 *   (Requirement #1/#3).
 * - `sonner`'s `toast.error()` for every query/mutation failure
 *   (Requirement #6/#8), consistent with every other sonner call site in
 *   this codebase (`src/app/routes/Subscription.tsx`,
 *   `src/app/routes/LoginEmail.tsx`, etc).
 *
 * TODO(FORMAT quality gate): `npx prettier --check src/lib/api src/stores`
 * fails on two files this issue does not otherwise touch —
 * `./fetchClient.ts` and `../../stores/impersonationStore.ts` — which
 * were already prettier-non-conformant before this issue (pre-existing
 * formatting drift, not a regression introduced here; verified via
 * `git diff`/`git status` showing zero content changes to either file in
 * this issue's changeset). Every file this issue actually adds/modifies
 * (`apiClient.ts`, `apiStore.ts`, `validation/admin.ts`, and the four
 * migrated `app/routes/admin/*.tsx` route components) passes `prettier
 * --check` cleanly. Deliberately not run through `prettier --write` here
 * per the AGENT rule to stay focused on this issue's files — running it
 * on `impersonationStore.ts` alone reformats ~220 of its ~290 lines
 * (a different/older prettier config era), which is a needless,
 * hard-to-review diff unrelated to this issue's scope. Follow-up: run
 * `npx prettier --write` across `src/lib/api` and `src/stores` in a
 * dedicated formatting-only change.
 */

import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

import { useApiFetch, ApiError } from './fetchClient';
import { useAuth } from '@/app/providers';
import { useApiStore, type ActiveImpersonationSession } from '@/stores/apiStore';
import {
  subscriptionMeSchema,
  adminSubscriptionsListSchema,
  buildAdminSubscriptionsQueryString,
  adminSubscriptionPlansListSchema,
  adminUnlinkedSubscriptionsListSchema,
  createCheckoutSessionResponseSchema,
  type SubscriptionMeDTO,
  type AdminSubscriptionsListResponse,
  type AdminSubscriptionsQueryParams,
  type AdminSubscriptionPlansListResponse,
  type AdminUnlinkedSubscriptionsListResponse,
  type CreateCheckoutSessionResponse,
} from '@/lib/validation/subscriptions';
import {
  adminDashboardResponseSchema,
  adminCustomersListSchema,
  adminJobsListSchema,
  adminJobDetailSchema,
  impersonateResponseSchema,
  endImpersonationResponseSchema,
  backfillSourcingResponseSchema,
  refreshRepliesResponseSchema,
  buildAdminCustomersQueryString,
  buildAdminJobsQueryString,
  buildAdminDashboardQueryString,
  type AdminDashboardResponse,
  type AdminCustomersListResponse,
  type AdminCustomersQueryParams,
  type AdminJobsListResponse,
  type AdminJobsQueryParams,
  type AdminJobDetailResponse,
  type BackfillSourcingResponse,
  type RefreshRepliesResponse,
} from '@/lib/validation/admin';
import {
  sourcingProgressResponseSchema,
  type SourcingProgressResponse,
} from '@/lib/validation/sourcingProgress';
import {
  dashboardStatsResponseSchema,
  type DashboardStatsResponse,
} from '@/lib/validation/dashboardStats';
import {
  pipelineAlertsResponseSchema,
  type PipelineAlertsResponse,
} from '@/lib/validation/pipelineAlerts';
import {
  activityFeedResponseSchema,
  type ActivityFeedResponse,
} from '@/lib/validation/activityFeed';
import { publicJobSchema, type PublicJobDTO } from '@/lib/validation/publicJob';
import {
  jobOutreachSettingsSchema,
  nextOutreachBatchResponseSchema,
  type JobOutreachSettings,
  type NextOutreachBatchResponse,
} from '@/lib/validation/jobOutreachTargeting';
import {
  createSupportTicketRequestSchema,
  supportTicketSchema,
  type CreateSupportTicketRequest,
  type SupportTicketDTO,
} from '@/lib/validation/support';
import {
  adminCustomerHealthResponseSchema,
  type AdminCustomerHealthResponse,
} from '@/lib/validation/customerHealth';
import {
  adminSendingDomainsResponseSchema,
  type AdminSendingDomainsResponse,
} from '@/lib/validation/sendingDomains';
import {
  adminJobPipelineResponseSchema,
  outreachMessageUpdateResponseSchema,
  sendOutreachResponseSchema,
  sendAllOutreachResponseSchema,
  syncRepliesResponseSchema,
  replyResponseSchema,
  bulkStatusChangeResponseSchema,
  bulkSendResponseSchema,
  bulkRegenerateResponseSchema,
  type AdminJobPipelineResponse,
  type OutreachMessageUpdateResponse,
  type SendOutreachResponse,
  type SendAllOutreachResponse,
  type SyncRepliesResponse,
  type ReplyResponse,
  type BulkStatusChangeResponse,
  type BulkSendResponse,
  type BulkRegenerateResponse,
  type OutreachMessageStatus,
} from '@/lib/validation/outreachPipeline';

/**
 * Shared `QueryClient` instance mounted once by `<QueryClientProvider>`
 * in `app/AppShell.tsx`. A plain `new QueryClient()` call is safe at
 * module scope (unlike a Supabase/Stripe/DB client): it touches no env
 * vars and performs no I/O on construction — it only becomes "live" once
 * a component actually calls one of the hooks below.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Query key factory — mirrors the hierarchical convention already used
 * by `src/lib/api/queryKeys.ts` (root `src/` tree). */
export const apiQueryKeys = {
  subscriptionMe: () => ['subscriptions', 'me'] as const,
  adminSubscriptions: (params: AdminSubscriptionsQueryParams) =>
    ['admin', 'subscriptions', params] as const,
  adminSubscriptionPlans: () => ['admin', 'subscription-plans'] as const,
  adminUnlinkedSubscriptions: () => ['admin', 'unlinked-subscriptions'] as const,
  adminCustomerHealth: (forceRefresh: boolean) =>
    ['admin', 'customer-health', forceRefresh] as const,
  adminSendingDomains: () => ['admin', 'sending-domains'] as const,
  adminDashboard: (userId: string) => ['admin', 'dashboard', userId] as const,
  adminCustomers: (params: AdminCustomersQueryParams) => ['admin', 'customers', params] as const,
  adminJobs: (params: AdminJobsQueryParams) => ['admin', 'jobs', params] as const,
  adminJobDetail: (jobId: string) => ['admin', 'jobs', 'detail', jobId] as const,
  sourcingProgress: (jobId: string) => ['jobs', jobId, 'sourcing-progress'] as const,
  jobOutreachSettings: (jobId: string) => ['jobs', jobId, 'outreach-settings'] as const,
  adminJobPipeline: (jobId: string) => ['admin', 'jobs', jobId, 'pipeline'] as const,
  publicJob: (jobId: string) => ['public', 'jobs', jobId] as const,
  dashboardStats: () => ['dashboard', 'stats'] as const,
  pipelineAlerts: () => ['dashboard', 'alerts'] as const,
  activityFeed: (jobId: string) => ['jobs', jobId, 'activity-feed'] as const,
};

/**
 * Extracts a human-readable message from a thrown query/mutation error,
 * preferring `ApiError`'s parsed backend message (Requirement #7's 401/
 * network-error handling: `ApiError.status` lets callers branch on
 * specific codes if they need to; the generic hooks below just surface
 * the message).
 */
function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

// ========================================================================
// GET /api/subscriptions/me
// ========================================================================

/**
 * The authenticated customer's own subscription — plan name, status,
 * expiry, jobs limit (`functions/api/subscriptions/me.ts`).
 *
 * Requirement #8: shows a `sonner` error toast on failure (once per
 * failed fetch, not once per re-render) via `onError`-equivalent
 * `useEffect` in the consuming component — TanStack Query v5 removed the
 * `onError` query option, so error-toast side effects live in the
 * component (see `app/routes/account/Subscription.tsx`), matching the
 * same pattern already established by `src/app/routes/Subscription.tsx`.
 */
export function useSubscriptionMe(): UseQueryResult<SubscriptionMeDTO, ApiError | Error> {
  const apiFetch = useApiFetch();
  const setLastFetchedAt = useApiStore((state) => state.setLastSubscriptionMeFetchedAt);

  return useQuery<SubscriptionMeDTO, ApiError | Error>({
    queryKey: apiQueryKeys.subscriptionMe(),
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/subscriptions/me');
      const parsed = subscriptionMeSchema.parse(raw);
      setLastFetchedAt(Date.now());
      return parsed;
    },
    retry: false,
  });
}

// ========================================================================
// POST /api/stripe/checkout-session — "Upgrade" button
// ========================================================================

/**
 * Creates a Stripe Checkout Session for the authenticated customer
 * (`functions/api/stripe/checkout-session.ts`'s `onRequestPost`, GitHub
 * issue #174, extended by "Checkout Sessions + stored keys — kill the
 * email-heuristic linkage" to also return the Stripe-hosted `url`).
 *
 * The caller (`app/routes/account/Subscription.tsx`'s "Upgrade" button)
 * redirects the whole browser to the returned `url`
 * (`window.location.href = url`) — a plain, no-`@stripe/stripe-js`-needed
 * redirect to `https://checkout.stripe.com/...`. Deliberately does NOT
 * send a `stripeCustomerId` in the request body: the backend always
 * resolves/creates the caller's OWN Stripe customer from their verified
 * auth token, never from client input (see
 * `assertCallerOwnsRequestedStripeCustomerId` in
 * `functions/api/stripe/checkout-session.ts` for the 403 guard against a
 * request that tried to).
 */
export function useCreateCheckoutSession(): UseMutationResult<
  CreateCheckoutSessionResponse,
  ApiError | Error,
  { planId: string }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ planId }) => {
      const raw = await apiFetch<unknown>('/stripe/checkout-session', {
        method: 'POST',
        json: { planId },
      });
      return createCheckoutSessionResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to start checkout. Please try again.'),
  });
}

// ========================================================================
// GET /api/admin/subscriptions
// ========================================================================

/**
 * Admin "total visibility" subscriptions dashboard data
 * (`apps/workers/src/api/admin/subscriptions.ts`, GH issue #166).
 *
 * @param params - Filter/sort/pagination state. Callers typically read
 * this straight from `useApiStore((s) => s.adminSubscriptionsFilters)`
 * (see `app/routes/admin/Subscriptions.tsx`) so that changing a filter
 * updates the query key and triggers a re-fetch automatically.
 */
export function useAdminSubscriptions(
  params: AdminSubscriptionsQueryParams
): UseQueryResult<AdminSubscriptionsListResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<AdminSubscriptionsListResponse, ApiError | Error>({
    queryKey: apiQueryKeys.adminSubscriptions(params),
    queryFn: async () => {
      const queryString = buildAdminSubscriptionsQueryString(params);
      const raw = await apiFetch<unknown>(`/admin/subscriptions?${queryString}`);
      return adminSubscriptionsListSchema.parse(raw);
    },
    retry: false,
  });
}

// ========================================================================
// GET /api/admin/subscription-plans
// ========================================================================

/**
 * Billing plan catalog (`apps/workers/src/api/admin/subscriptions.ts`'s
 * `listAdminSubscriptionPlans`, GitHub issue #197 — "subscription_plans
 * has placeholder Stripe price IDs -> every paying customer provisioned
 * on FREE Basic plan"). Distinct from {@link useAdminSubscriptions} (a
 * per-customer subscription list) — this returns the plan catalog itself
 * (name, Stripe Price ID, price) so an admin can directly verify no
 * placeholder/`$0` rows remain for paid plans.
 */
export function useAdminSubscriptionPlans(): UseQueryResult<
  AdminSubscriptionPlansListResponse,
  ApiError | Error
> {
  const apiFetch = useApiFetch();

  return useQuery<AdminSubscriptionPlansListResponse, ApiError | Error>({
    queryKey: apiQueryKeys.adminSubscriptionPlans(),
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/admin/subscription-plans');
      return adminSubscriptionPlansListSchema.parse(raw);
    },
    retry: false,
  });
}

// ========================================================================
// GET /api/admin/unlinked-subscriptions
// ========================================================================

/**
 * Pending Stripe->user linkage gaps
 * (`apps/workers/src/api/admin/subscriptions.ts`'s
 * `listAdminUnlinkedSubscriptions`) — bug: "Stripe webhook — paying
 * customer has NO user record, subscription creation failing since Jun
 * 9" (subscription `sub_1TgNV6Lj957TeqndmPVByhfE`). Defaults to only the
 * still-`unlinked` rows, matching the backend's default filter.
 */
export function useAdminUnlinkedSubscriptions(): UseQueryResult<
  AdminUnlinkedSubscriptionsListResponse,
  ApiError | Error
> {
  const apiFetch = useApiFetch();

  return useQuery<AdminUnlinkedSubscriptionsListResponse, ApiError | Error>({
    queryKey: apiQueryKeys.adminUnlinkedSubscriptions(),
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/admin/unlinked-subscriptions');
      return adminUnlinkedSubscriptionsListSchema.parse(raw);
    },
    retry: false,
  });
}

// ========================================================================
// GET /api/admin/customer-health
// ========================================================================

/**
 * Per-customer health dashboard — Stripe billing × platform activity
 * (jobs -> sourced -> sent -> replies), joined server-side
 * (`apps/workers/src/api/admin/customerHealth.ts`, GitHub issue "[Admin]
 * Per-customer health dashboard"). Backend caches its own Stripe/DB pull
 * for up to 1h; `forceRefresh` maps to that endpoint's `?refresh=1`
 * cache-bypass for an admin who wants the very latest numbers
 * immediately (see `app/routes/admin/CustomerHealth.tsx`'s "Refresh
 * now" button).
 */
export function useAdminCustomerHealth(
  forceRefresh = false
): UseQueryResult<AdminCustomerHealthResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<AdminCustomerHealthResponse, ApiError | Error>({
    queryKey: apiQueryKeys.adminCustomerHealth(forceRefresh),
    queryFn: async () => {
      const path = forceRefresh ? '/admin/customer-health?refresh=1' : '/admin/customer-health';
      const raw = await apiFetch<unknown>(path);
      return adminCustomerHealthResponseSchema.parse(raw);
    },
    retry: false,
  });
}

// ========================================================================
// GET /api/admin/sending-domains
// ========================================================================

/**
 * Active cold-outreach rotation domains + per-domain SPF/DKIM/DMARC
 * verification status (`apps/workers/src/api/admin/sendingDomains.ts`,
 * GitHub issue "[Email] Move cold outreach off the brand domain —
 * dedicated, warmed, rotated sending domains", #188/#516). Backs
 * `app/routes/admin/SendingDomains.tsx`.
 */
export function useAdminSendingDomains(): UseQueryResult<AdminSendingDomainsResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<AdminSendingDomainsResponse, ApiError | Error>({
    queryKey: apiQueryKeys.adminSendingDomains(),
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/admin/sending-domains');
      return adminSendingDomainsResponseSchema.parse(raw);
    },
    retry: false,
  });
}

// ========================================================================
// GET /api/admin/dashboard
// ========================================================================

/**
 * Aggregate platform metrics + conversion funnel for the admin console's
 * top-level dashboard (`apps/workers/src/api/admin/dashboard.ts`, GH
 * issue #284). Replaces `app/routes/admin/AdminDashboard.tsx`'s previous
 * hard-coded mock `AdminDashboardMetrics`/`AdminFunnelMetrics` behind a
 * `// TODO: Call GET /api/admin/dashboard` comment.
 *
 * @param userId - GitHub issue #563's customer-scope filter: the
 * `owner_id` of the `AdminCustomerInfo` currently selected in the
 * dashboard's customer dropdown (`useApiStore`'s
 * `adminDashboardCustomerId`), or `''` for the platform-wide view. Included
 * in the query key so selecting a different customer automatically
 * refetches — this is this issue's actual fix: previously the dashboard's
 * Opened/Bounced cards were fetched from a query key that never varied
 * with the customer selection, so they could never reflect it.
 */
export function useAdminDashboard(
  userId: string = ''
): UseQueryResult<AdminDashboardResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<AdminDashboardResponse, ApiError | Error>({
    queryKey: apiQueryKeys.adminDashboard(userId),
    queryFn: async () => {
      const queryString = buildAdminDashboardQueryString({ userId });
      const path = queryString ? `/admin/dashboard?${queryString}` : '/admin/dashboard';
      const raw = await apiFetch<unknown>(path);
      return adminDashboardResponseSchema.parse(raw);
    },
    retry: false,
  });
}

// ========================================================================
// GET /api/admin/customers
// ========================================================================

/**
 * Admin "total visibility" customers list — search/filter/paginate every
 * tenant's users (`apps/workers/src/api/admin/list.ts`'s `listCustomers`,
 * GH issue #284). Replaces `app/routes/admin/CustomersList.tsx`'s
 * previous hard-coded mock `AdminCustomerInfo[]` + client-side
 * search/filter/pagination behind a `// TODO: Call GET
 * /api/admin/customers with filters` comment.
 *
 * @param params - Filter/search/pagination state, typically read from
 * `useApiStore((s) => s.adminCustomersFilters)` so changing a filter
 * updates the query key and triggers a re-fetch automatically.
 */
export function useAdminCustomers(
  params: AdminCustomersQueryParams
): UseQueryResult<AdminCustomersListResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<AdminCustomersListResponse, ApiError | Error>({
    queryKey: apiQueryKeys.adminCustomers(params),
    queryFn: async () => {
      const queryString = buildAdminCustomersQueryString(params);
      const raw = await apiFetch<unknown>(`/admin/customers?${queryString}`);
      return adminCustomersListSchema.parse(raw);
    },
    retry: false,
  });
}

// ========================================================================
// GET /api/admin/jobs, GET /api/admin/jobs/:jobId
// ========================================================================

/**
 * Admin "total visibility" jobs list — search/filter/sort/paginate every
 * tenant's jobs (`apps/workers/src/api/admin/list.ts`'s `listJobs`, GH
 * issue #284). Replaces `app/routes/admin/JobsList.tsx`'s previous
 * hard-coded mock `AdminJobInfo[]`.
 *
 * @param params - Filter/search/sort/pagination state, typically read
 * from `useApiStore((s) => s.adminJobsFilters)`.
 */
export function useAdminJobs(
  params: AdminJobsQueryParams
): UseQueryResult<AdminJobsListResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<AdminJobsListResponse, ApiError | Error>({
    queryKey: apiQueryKeys.adminJobs(params),
    queryFn: async () => {
      const queryString = buildAdminJobsQueryString(params);
      const raw = await apiFetch<unknown>(`/admin/jobs?${queryString}`);
      return adminJobsListSchema.parse(raw);
    },
    retry: false,
  });
}

/**
 * Single job drill-down (`apps/workers/src/api/admin/list.ts`'s
 * `getJobDetail`, GH issue #284). Replaces
 * `app/routes/admin/JobDetail.tsx`'s previous hard-coded mock `AdminJobInfo`
 * (its `route state`-provided job, passed by `JobsList`'s row click, is
 * still used as an initial value — see `initialData` below — so
 * navigating from the list doesn't show a loading flash for data the
 * caller already has).
 *
 * @param jobId - Job UUID (`useParams<{ jobId: string }>()`).
 * @param initialData - Optional job data already known by the caller
 * (e.g. passed via `react-router` `location.state`), seeded as this
 * query's `initialData` so it renders immediately while a background
 * refetch confirms freshness.
 */
export function useAdminJobDetail(
  jobId: string | undefined,
  initialData?: AdminJobDetailResponse
): UseQueryResult<AdminJobDetailResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<AdminJobDetailResponse, ApiError | Error>({
    queryKey: apiQueryKeys.adminJobDetail(jobId ?? ''),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`/admin/jobs/${jobId}`);
      return adminJobDetailSchema.parse(raw);
    },
    enabled: Boolean(jobId),
    initialData,
    retry: false,
  });
}

// ========================================================================
// GET /api/jobs/:jobId/sourcing-progress
// ========================================================================

/**
 * Polls the sourcing-progress tracker shown on `/job-published`
 * (`app/routes/JobPublished.tsx`) — see `@/lib/validation/sourcingProgress`'s
 * module doc-comment for the backend TODO. Polls every 3s via
 * `refetchInterval` until the response reports `isComplete: true`, at
 * which point polling stops (matches the ticket's "Stages animate through
 * correctly (mock polling response)" / "Complete state shows final counts"
 * test cases).
 */
export function useSourcingProgress(
  jobId: string | undefined
): UseQueryResult<SourcingProgressResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<SourcingProgressResponse, ApiError | Error>({
    queryKey: apiQueryKeys.sourcingProgress(jobId ?? ''),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`/jobs/${jobId}/sourcing-progress`);
      return sourcingProgressResponseSchema.parse(raw);
    },
    enabled: Boolean(jobId),
    retry: false,
    refetchInterval: (query) => (query.state.data?.isComplete ? false : 3000),
  });
}

// ========================================================================
// GET /api/jobs/:jobId — top-N outreach targeting (job dashboard)
// ========================================================================

/** Envelope shape both `GET /api/jobs/:jobId` and `PATCH /api/jobs/:jobId`
 *  respond with (`apps/workers/src/api/jobs.ts`). */
const jobOutreachSettingsEnvelopeSchema = z.object({
  success: z.boolean(),
  data: jobOutreachSettingsSchema,
});

/**
 * A job's current outreach-targeting configuration (GitHub issue "[Email]
 * Top-N targeting — queue outreach only for the top-N best-fit candidates
 * per job") — backs the job dashboard's "top-N" control on
 * `@/pages/Dashboard.tsx`.
 */
export function useJobOutreachSettings(
  jobId: string | undefined
): UseQueryResult<JobOutreachSettings, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<JobOutreachSettings, ApiError | Error>({
    queryKey: apiQueryKeys.jobOutreachSettings(jobId ?? ''),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`/jobs/${jobId}`);
      return jobOutreachSettingsEnvelopeSchema.parse(raw).data;
    },
    enabled: Boolean(jobId),
    retry: false,
  });
}

/**
 * Saves a new `outreach_top_n` value for a job (the dashboard's "top-N"
 * control's "Save" action) via the existing `PATCH /api/jobs/:jobId`
 * endpoint. On success, refreshes `useJobOutreachSettings` so the saved
 * value is what re-renders (and what a page reload re-fetches) — this
 * issue's demo step "the configured value persists and displays '25'
 * after reload".
 */
export function useUpdateJobOutreachTopN(): UseMutationResult<
  JobOutreachSettings,
  ApiError | Error,
  { jobId: string; outreachTopN: number }
> {
  const apiFetch = useApiFetch();
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, outreachTopN }) => {
      const raw = await apiFetch<unknown>(`/jobs/${jobId}`, {
        method: 'PATCH',
        json: { outreach_top_n: outreachTopN },
      });
      return jobOutreachSettingsEnvelopeSchema.parse(raw).data;
    },
    onSuccess: (data, variables) => {
      client.setQueryData(apiQueryKeys.jobOutreachSettings(variables.jobId), data);
    },
    onError: (error) => reportApiError(error, 'Failed to save the outreach top-N setting'),
  });
}

// ========================================================================
// POST /api/jobs/:jobId/outreach/next-batch — "Send next batch"
// ========================================================================

const nextOutreachBatchEnvelopeSchema = z.object({
  success: z.boolean(),
  data: nextOutreachBatchResponseSchema,
});

/**
 * "Send next batch" (GitHub issue "[Email] Top-N targeting"): advances
 * the next `outreach_top_n` best-fit candidates for a job into
 * `OUTREACH_QUEUED` on demand. Invalidates
 * {@link apiQueryKeys.sourcingProgress} so the dashboard's "Queued for
 * outreach" count reflects the new total on the next refetch.
 */
export function useSendNextOutreachBatch(): UseMutationResult<
  NextOutreachBatchResponse,
  ApiError | Error,
  { jobId: string }
> {
  const apiFetch = useApiFetch();
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId }) => {
      const raw = await apiFetch<unknown>(`/jobs/${jobId}/outreach/next-batch`, {
        method: 'POST',
      });
      return nextOutreachBatchEnvelopeSchema.parse(raw).data;
    },
    onSuccess: (_data, variables) => {
      client.invalidateQueries({ queryKey: apiQueryKeys.sourcingProgress(variables.jobId) });
    },
    onError: (error) => reportApiError(error, 'Failed to send the next outreach batch'),
  });
}

// ========================================================================
// GET /api/dashboard/stats
// ========================================================================

/**
 * User-scoped dashboard stats (`apps/workers/src/api/dashboardStats.ts`,
 * GitHub issue "bug: regular users see wrong dashboard numbers —
 * admin-only stats endpoint" — Luisa Franco / LFP Risk Solutions). Backs
 * the three stat tiles on `@/pages/Dashboard.tsx`, replacing their
 * previous hard-coded `0`s. Callable by ANY authenticated user (unlike
 * {@link useAdminDashboard}, which requires superuser status) — the
 * backend scopes every count to the caller's own jobs via Postgres RLS,
 * using the caller's own JWT rather than the service-role key.
 */
export function useDashboardStats(): UseQueryResult<DashboardStatsResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<DashboardStatsResponse, ApiError | Error>({
    queryKey: apiQueryKeys.dashboardStats(),
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/dashboard/stats');
      return dashboardStatsResponseSchema.parse(raw);
    },
    retry: false,
  });
}

// ========================================================================
// GET /api/dashboard/alerts
// ========================================================================

/**
 * Customer-facing pipeline status / stale-progress alerts panel
 * (`apps/workers/src/api/pipelineAlerts.ts`, GitHub issue "P1: No stale
 * pipeline alerting — customers stuck for weeks with no notification").
 * Backs the stale-pipeline warning banner on `@/pages/Dashboard.tsx`.
 * Callable by ANY authenticated user — scoped to the caller's own jobs'
 * alerts via Postgres RLS, same as {@link useDashboardStats}.
 */
export function usePipelineAlerts(): UseQueryResult<PipelineAlertsResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<PipelineAlertsResponse, ApiError | Error>({
    queryKey: apiQueryKeys.pipelineAlerts(),
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/dashboard/alerts');
      return pipelineAlertsResponseSchema.parse(raw);
    },
    retry: false,
  });
}

// ========================================================================
// GET /api/jobs/:jobId/activity-feed
// ========================================================================

/**
 * Customer-facing activity feed panel (`apps/workers/src/api/jobs/activityFeed.ts`,
 * GitHub issue "P0: Customer churn — Tom ... dashboard stale, pipeline
 * broken"). Backs the activity feed panel on `@/pages/Dashboard.tsx` —
 * scoped to the caller's own job via Postgres RLS, same pattern as
 * {@link useSourcingProgress}.
 */
export function useActivityFeed(
  jobId: string | undefined
): UseQueryResult<ActivityFeedResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<ActivityFeedResponse, ApiError | Error>({
    queryKey: apiQueryKeys.activityFeed(jobId ?? ''),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`/jobs/${jobId}/activity-feed`);
      return activityFeedResponseSchema.parse(raw);
    },
    enabled: Boolean(jobId),
    retry: false,
  });
}

// ========================================================================
// GET /api/public/jobs/:jobId
// ========================================================================

/**
 * Public (no-auth) job detail — backs `/jobs/:jobId`
 * (`app/routes/PublicJobPage.tsx`), the entry point for GitHub issue
 * #301's JobPosting schema.org markup. Unlike every other hook in this
 * file, this one is expected to be called from a route that renders
 * without a signed-in session, so a missing/expired access token is fine
 * (`useApiFetch` simply omits the `Authorization` header — see
 * `./fetchClient.ts`).
 *
 * `apps/workers/src/api/publicJobs.ts`'s `GET /api/public/jobs/:jobId`
 * returns 410 for an ARCHIVED (`status: 'CLOSED'`) job and 404 for a
 * not-yet-published/DRAFT or nonexistent job — both surface here as a
 * thrown `ApiError` with the matching `.status`, which
 * `PublicJobPage.tsx` branches on to render the "this posting has been
 * removed" state instead of a generic error.
 */
export function usePublicJob(
  jobId: string | undefined
): UseQueryResult<PublicJobDTO, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<PublicJobDTO, ApiError | Error>({
    queryKey: apiQueryKeys.publicJob(jobId ?? ''),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`/public/jobs/${jobId}`);
      return publicJobSchema.parse(raw);
    },
    enabled: Boolean(jobId),
    retry: false,
  });
}

// ========================================================================
// GET /api/admin/jobs/:jobId/pipeline
// ========================================================================

/**
 * The combined candidate + outreach-thread pipeline for a job's admin
 * detail view (`components/admin/CandidateOutreachPipeline.tsx`). See
 * `@/lib/validation/outreachPipeline`'s module doc-comment for the backend
 * TODO this supersedes (`JobDetail.tsx`'s previous separate
 * `candidates`/`outreachMessages` mock-`useEffect` blocks).
 */
export function useAdminJobPipeline(
  jobId: string | undefined
): UseQueryResult<AdminJobPipelineResponse, ApiError | Error> {
  const apiFetch = useApiFetch();

  return useQuery<AdminJobPipelineResponse, ApiError | Error>({
    queryKey: apiQueryKeys.adminJobPipeline(jobId ?? ''),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(`/admin/jobs/${jobId}/pipeline`);
      return adminJobPipelineResponseSchema.parse(raw);
    },
    enabled: Boolean(jobId),
    retry: false,
  });
}

// ========================================================================
// Outreach pipeline mutations (edit / regenerate / send / replies / bulk /
// auto-followup) — all best-effort network calls: every mutation's
// `onError` is a no-op (rather than reverting/blocking the UI) so the
// optimistic local-state update the calling component already applied
// stands even against the not-yet-implemented backend (see
// `@/lib/validation/outreachPipeline`'s module doc-comment). Once real
// endpoints exist, tighten these to properly roll back on failure.
// ========================================================================

export function useEditOutreachEmail(): UseMutationResult<
  OutreachMessageUpdateResponse,
  ApiError | Error,
  { messageId: string; body: string }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ messageId, body }) => {
      const raw = await apiFetch<unknown>(`/admin/outreach/${messageId}`, {
        method: 'PATCH',
        json: { body },
      });
      return outreachMessageUpdateResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to save email (kept local edit)'),
  });
}

export function useRegenerateOutreachEmail(): UseMutationResult<
  OutreachMessageUpdateResponse,
  ApiError | Error,
  { messageId: string; instructions: string }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ messageId, instructions }) => {
      const raw = await apiFetch<unknown>(`/admin/outreach/${messageId}/regenerate`, {
        method: 'POST',
        json: { instructions },
      });
      return outreachMessageUpdateResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to regenerate email'),
  });
}

export function useSendOutreachEmail(): UseMutationResult<
  SendOutreachResponse,
  ApiError | Error,
  { messageId: string }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ messageId }) => {
      const raw = await apiFetch<unknown>(`/admin/outreach/${messageId}/send`, {
        method: 'POST',
      });
      return sendOutreachResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to send email'),
  });
}

export function useSendAllQueuedOutreach(): UseMutationResult<
  SendAllOutreachResponse,
  ApiError | Error,
  { jobId: string; messageIds: string[] }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ jobId, messageIds }) => {
      const raw = await apiFetch<unknown>(`/admin/jobs/${jobId}/outreach/send-all`, {
        method: 'POST',
        json: { messageIds },
      });
      return sendAllOutreachResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to send queued emails'),
  });
}

export function useSyncReplies(): UseMutationResult<
  SyncRepliesResponse,
  ApiError | Error,
  { jobId: string }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ jobId }) => {
      const raw = await apiFetch<unknown>(`/admin/jobs/${jobId}/outreach/sync-replies`, {
        method: 'POST',
      });
      return syncRepliesResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to sync replies'),
  });
}

export function useComposeReply(): UseMutationResult<
  ReplyResponse,
  ApiError | Error,
  { messageId: string; body: string }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ messageId, body }) => {
      const raw = await apiFetch<unknown>(`/admin/outreach/${messageId}/replies`, {
        method: 'POST',
        json: { body },
      });
      return replyResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to send reply'),
  });
}

export function useAiGenerateReply(): UseMutationResult<
  ReplyResponse,
  ApiError | Error,
  { messageId: string }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ messageId }) => {
      const raw = await apiFetch<unknown>(`/admin/outreach/${messageId}/replies/generate`, {
        method: 'POST',
      });
      return replyResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to AI-generate a reply draft'),
  });
}

export function useBulkStatusChange(): UseMutationResult<
  BulkStatusChangeResponse,
  ApiError | Error,
  { candidateIds: string[]; status: OutreachMessageStatus }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ candidateIds, status }) => {
      const raw = await apiFetch<unknown>('/admin/candidates/bulk-status', {
        method: 'PATCH',
        json: { candidateIds, status },
      });
      return bulkStatusChangeResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to update candidate statuses'),
  });
}

export function useBulkSendOutreach(): UseMutationResult<
  BulkSendResponse,
  ApiError | Error,
  { messageIds: string[] }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ messageIds }) => {
      const raw = await apiFetch<unknown>('/admin/outreach/bulk-send', {
        method: 'POST',
        json: { messageIds },
      });
      return bulkSendResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to bulk-send emails'),
  });
}

export function useBulkRegenerateOutreach(): UseMutationResult<
  BulkRegenerateResponse,
  ApiError | Error,
  { messageIds: string[]; instructions: string }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ messageIds, instructions }) => {
      const raw = await apiFetch<unknown>('/admin/outreach/bulk-regenerate', {
        method: 'POST',
        json: { messageIds, instructions },
      });
      return bulkRegenerateResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to bulk-regenerate emails'),
  });
}

export function useToggleCandidateAutoFollowup(): UseMutationResult<
  { candidateId: string; optOut: boolean },
  ApiError | Error,
  { candidateId: string; optOut: boolean }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ candidateId, optOut }) => {
      await apiFetch<unknown>(`/admin/candidates/${candidateId}/auto-followup`, {
        method: 'PATCH',
        json: { optOut },
      });
      return { candidateId, optOut };
    },
    onError: (error) => reportApiError(error, 'Failed to update auto-followup setting'),
  });
}

export function useToggleJobAutoFollowup(): UseMutationResult<
  { jobId: string; enabled: boolean },
  ApiError | Error,
  { jobId: string; enabled: boolean }
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async ({ jobId, enabled }) => {
      await apiFetch<unknown>(`/admin/jobs/${jobId}/auto-followup`, {
        method: 'PATCH',
        json: { enabled },
      });
      return { jobId, enabled };
    },
    onError: (error) => reportApiError(error, 'Failed to update job auto-followup setting'),
  });
}

// ========================================================================
// POST /api/admin/impersonate, POST /api/admin/impersonate/end
// ========================================================================

/**
 * Starts an admin impersonation session for a given customer
 * (`apps/workers/src/api/admin/impersonate.ts`'s `impersonate`, GH issue
 * #281/#302). On success, writes the session (sans JWT `token`) into
 * `useApiStore`'s `impersonationSession` (Requirement: "when the
 * mutation succeeds, then the Zustand store updates accordingly") and
 * invalidates the admin customers list cache — an impersonated
 * customer's `last_login` may change as a side effect of the
 * impersonated session, so the next customers-list view should refetch
 * rather than serve a stale cached page (Requirement: "the relevant
 * TanStack Query caches are invalidated ... to reflect the new state").
 */
export function useStartImpersonation(): UseMutationResult<
  ActiveImpersonationSession,
  ApiError | Error,
  { impersonatedUserId: string }
> {
  const apiFetch = useApiFetch();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const setImpersonationSession = useApiStore((state) => state.setImpersonationSession);

  return useMutation<ActiveImpersonationSession, ApiError | Error, { impersonatedUserId: string }>({
    mutationFn: async ({ impersonatedUserId }) => {
      const raw = await apiFetch<unknown>('/admin/impersonate', {
        method: 'POST',
        json: { impersonatedUserId },
        headers: user?.id ? { 'X-Admin-User-ID': user.id } : undefined,
      });
      const parsed = impersonateResponseSchema.parse(raw);
      // Deliberately omit `parsed.token` (the JWT) from client state — see
      // `ActiveImpersonationSession`'s doc-comment (`@/stores/apiStore`).
      const session: ActiveImpersonationSession = {
        sessionId: parsed.sessionId,
        expiresAt: parsed.expiresAt,
        impersonatedUserId: parsed.impersonatedUserId,
      };
      return session;
    },
    onSuccess: (session) => {
      setImpersonationSession(session);
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
    onError: (error) => {
      reportApiError(error, 'Failed to start impersonation session');
    },
  });
}

/**
 * Ends/revokes the active admin impersonation session
 * (`apps/workers/src/api/admin/impersonate.ts`'s `endImpersonation`).
 * On success, clears `useApiStore`'s `impersonationSession`.
 */
export function useEndImpersonation(): UseMutationResult<
  void,
  ApiError | Error,
  { sessionId: string }
> {
  const apiFetch = useApiFetch();
  const { user } = useAuth();
  const clearImpersonationSession = useApiStore((state) => state.clearImpersonationSession);

  return useMutation<void, ApiError | Error, { sessionId: string }>({
    mutationFn: async ({ sessionId }) => {
      const raw = await apiFetch<unknown>('/admin/impersonate/end', {
        method: 'POST',
        json: { sessionId },
        headers: user?.id ? { 'X-Admin-User-ID': user.id } : undefined,
      });
      endImpersonationResponseSchema.parse(raw);
    },
    onSuccess: () => {
      clearImpersonationSession();
    },
    onError: (error) => {
      reportApiError(error, 'Failed to end impersonation session');
    },
  });
}

// ========================================================================
// POST /api/admin/sourcing/backfill, POST /api/admin/outreach/refresh-replies
// ========================================================================

/**
 * "Backfill Sourcing" button (`AdminDashboard.tsx`, GitHub issue #499 —
 * "E2E: Admin dashboard — outreach metrics, company filter, job table",
 * "Actions" test-case group). No real backend route exists for this yet
 * (see `@/lib/validation/admin`'s `backfillSourcingResponseSchema`
 * doc-comment) — like several other admin mutations in this file before
 * their backend landed (e.g. `useSyncReplies` at the time `useAdminJobs`
 * itself was still mock data), this fires a real request so the button
 * is wired end-to-end and `e2e/admin-dashboard.spec.ts` can assert on it
 * via `page.waitForRequest`; a 404 in a real (non-test) environment
 * degrades to the existing `reportApiError` toast rather than throwing.
 */
export function useBackfillSourcing(): UseMutationResult<
  BackfillSourcingResponse,
  ApiError | Error,
  void
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async () => {
      const raw = await apiFetch<unknown>('/admin/sourcing/backfill', { method: 'POST' });
      return backfillSourcingResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to trigger sourcing backfill'),
  });
}

/**
 * "Refresh Replies" button (`AdminDashboard.tsx`, same ticket/test-case
 * group as {@link useBackfillSourcing}). Distinct from the already-wired
 * per-job `useSyncReplies()` (`POST
 * /admin/jobs/:jobId/outreach/sync-replies`) — this refreshes replies
 * across every job from the top-level dashboard.
 */
export function useRefreshReplies(): UseMutationResult<
  RefreshRepliesResponse,
  ApiError | Error,
  void
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async () => {
      const raw = await apiFetch<unknown>('/admin/outreach/refresh-replies', { method: 'POST' });
      return refreshRepliesResponseSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to refresh replies'),
  });
}

/**
 * Fires a `sonner` error toast for a failed query, once per distinct
 * error instance. Intended to be called from a component's `useEffect`
 * keyed on `[isError, error]` — see both migrated route components for
 * the exact pattern (Requirement #8).
 */
export function reportApiError(error: unknown, fallbackMessage: string): void {
  toast.error(toErrorMessage(error, fallbackMessage));
}

// ========================================================================
// POST /api/v1/support
// ========================================================================

/**
 * Submit an internal customer support ticket
 * (`apps/workers/src/api/support.ts`, GitHub issue #544). Replaces the
 * Slack shared-invite link as the customer support channel — see
 * `app/routes/Support.tsx`'s module doc-comment for the full "why".
 */
export function useSubmitSupportTicket(): UseMutationResult<
  SupportTicketDTO,
  ApiError | Error,
  CreateSupportTicketRequest
> {
  const apiFetch = useApiFetch();

  return useMutation({
    mutationFn: async (request) => {
      const validated = createSupportTicketRequestSchema.parse(request);
      const raw = await apiFetch<unknown>('/v1/support', {
        method: 'POST',
        json: validated,
      });
      return supportTicketSchema.parse(raw);
    },
    onError: (error) => reportApiError(error, 'Failed to submit support request'),
  });
}
